import { ColumnDef } from "@tanstack/react-table";
import { ChevronLeft } from "baseui/icon";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "../../../../components/button/Button";
import { SettingsIconButton } from "../../../../components/button/SettingsIconButton";
import { SettingsCard } from "../../../../components/card/SettingsCard";
import { SettingsDropdown } from "../../../../components/dropdown/SettingsDropdown";
import { LottieLoading } from "../../../../components/graphics/LottieLoading";
import { SettingsConfirmModal } from "../../../../components/modal/SettingsConfirmModal";
import { DotsSvg } from "../../../../components/svg/DotsSvg";
import { EditPencilSvg } from "../../../../components/svg/EditPencilSvg";
import { TrashSvg } from "../../../../components/svg/TrashSvg";
import { Table } from "../../../../components/table/Table";
import {
  IService,
  useAddServiceMutation,
  useDeleteServiceMutation,
  useGetServicesQuery,
  useUpdateServiceMutation,
} from "../../../../services/BillingService";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";

export const Services = () => {
  const navigate = useNavigate();
  const orgId = getLinkedFirm()?.orgId;

  const { data: services, isLoading } = useGetServicesQuery(orgId, {
    skip: !orgId,
  });

  const [addService] = useAddServiceMutation();
  const [updateService, { isSuccess: isSuccessUpdate }] =
    useUpdateServiceMutation();
  const [deleteService, { isSuccess: isSuccessDelete, isLoading: isDeleting }] =
    useDeleteServiceMutation();
  const [serviceToDelete, setServiceToDelete] = useState<IService | null>(null);

  const handleDeleteService = async () => {
    if (!serviceToDelete) {
      return;
    }

    await deleteService(serviceToDelete.id);
    setServiceToDelete(null);
  };

  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenUpdate, setIsOpenUpdate] = useState(false);

  const [newServiceData, setNewServiceData] = useState({
    id: "",
    serviceName: "",
    description: "",
    rate: 0,
  });

  const [error, setError] = useState("");
  const [errorRate, setErrorRate] = useState("");
  const [rateInput, setRateInput] = useState("");
  const [isRateFocused, setIsRateFocused] = useState(false);

  const resetServiceForm = () => {
    setNewServiceData({
      id: "",
      serviceName: "",
      description: "",
      rate: 0,
    });
    setError("");
    setErrorRate("");
    setRateInput("");
    setIsRateFocused(false);
  };

  const closeAddModal = () => {
    setIsOpenAdd(false);
    resetServiceForm();
  };

  const closeUpdateModal = () => {
    setIsOpenUpdate(false);
    resetServiceForm();
  };

  useEffect(() => {
    if (isSuccessUpdate || isSuccessDelete) {
      closeUpdateModal();
    }
  }, [isSuccessUpdate, isSuccessDelete]);

  const handleRateFocus = () => {
    setIsRateFocused(true);
    setRateInput(
      newServiceData.rate && newServiceData.rate !== 0
        ? String(newServiceData.rate)
        : "",
    );
  };

  const handleRateChange = (value: string) => {
    const raw = value.replace("$", "");

    if (/^\d*\.?\d{0,2}$/.test(raw)) {
      setRateInput(raw);
      setNewServiceData((prev) => ({
        ...prev,
        rate: raw === "" ? 0 : Number(raw),
      }));
    }
  };

  const handleRateBlur = () => {
    setIsRateFocused(false);

    if (!newServiceData.rate || newServiceData.rate === 0) {
      setErrorRate("Rate is required");
    } else {
      setErrorRate("");
    }
  };

  const validateServiceForm = () => {
    setError("");
    setErrorRate("");

    if (!newServiceData.serviceName.trim()) {
      setError("This field cannot be empty");
      return false;
    }

    if (!newServiceData.rate || newServiceData.rate === 0) {
      setErrorRate("Rate is required");
      return false;
    }

    return true;
  };

  const renderRateInput = () => (
    <input
      maxLength={25}
      type={"text"}
      className={"input input-bordered h-[32px] w-full text-sm"}
      value={
        isRateFocused
          ? rateInput
          : `$${Number(newServiceData.rate || 0).toFixed(2)}`
      }
      onChange={(e) => handleRateChange(e.target.value)}
      onFocus={handleRateFocus}
      onBlur={handleRateBlur}
    />
  );

  const columns = useMemo<ColumnDef<IService>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Service",
        sortingFn: "alphanumeric",
        cell: ({ row }) => (
          <div className={"font-semibold text-[#1D2C3B]"}>
            {row.original.name}
          </div>
        ),
        meta: {
          className: "text-sm",
        },
      },
      {
        accessorKey: "description",
        header: "Description",
        sortingFn: "alphanumeric",
        cell: ({ getValue }) => (
          <span className={"text-sm text-[#3F5266]"}>
            {getValue<string>() || "—"}
          </span>
        ),
        meta: {
          className: "text-sm text-[#3F5266]",
        },
      },
      {
        accessorKey: "rate",
        header: "Rate",
        sortingFn: "basic",
        cell: ({ getValue }) => (
          <span className={"text-sm font-medium text-[#3F5266]"}>
            {Number(getValue<number>() || 0).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        ),
        meta: {
          className: "text-sm font-medium text-[#3F5266]",
        },
      },
      {
        accessorKey: "createdAt",
        header: "Date Created",
        sortingFn: "datetime",
        cell: ({ getValue }) => (
          <span className={"text-sm font-normal text-[#3F5266]"}>
            {moment(getValue<string>()).format("DD MMM YYYY")}
          </span>
        ),
        meta: {
          className: "text-sm font-normal text-[#3F5266]",
        },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <SettingsDropdown
            actionsList={[
              {
                name: "Edit",
                action: () => {
                  const service = row.original;

                  setIsOpenUpdate(true);
                  setNewServiceData({
                    id: service.id,
                    serviceName: service.name,
                    rate: service.rate,
                    description: service.description,
                  });
                  setRateInput(String(service.rate ?? ""));
                  setIsRateFocused(false);
                  setError("");
                  setErrorRate("");
                },
                icon: <EditPencilSvg />,
              },
              {
                name: "Delete",
                action: () => setServiceToDelete(row.original),
                icon: <TrashSvg />,
                extraClasses: "text-[#DC2626]",
                iconColor: "text-[#DC2626]",
              },
            ]}>
            <SettingsIconButton
              icon={<DotsSvg />}
              onClick={() => null}
              size={"small"}
            />
          </SettingsDropdown>
        ),
      },
    ],
    [],
  );

  const renderAdd = () => (
    <div className={"modal modal-open"}>
      <div className={"modal-box w-[450px] p-0"}>
        <div
          className={
            "mb-4 flex w-full items-center justify-start border-b border-gray-200 px-4 py-2 text-center text-base font-semibold"
          }>
          Create Service
        </div>

        <div className={"mb-4 px-6"}>
          <div className={"mb-2 text-sm"}>Service Name</div>
          <input
            required
            maxLength={25}
            type={"text"}
            className={"input input-bordered h-[32px] w-full text-sm"}
            value={newServiceData.serviceName}
            onBlur={(event) => {
              if (!event.target.value.trim()) {
                setError("This field cannot be empty");
              } else {
                setError("");
              }
            }}
            onChange={(event) => {
              setNewServiceData((prev) => ({
                ...prev,
                serviceName: event.target.value,
              }));
            }}
          />
          {error && (
            <div className={"mt-2 text-sm font-semibold text-error"}>
              {error}
            </div>
          )}
        </div>

        <div className={"mb-4 px-6"}>
          <div className={"mb-2 text-sm"}>Description (optional)</div>
          <textarea
            maxLength={200}
            className={
              "input input-bordered h-[85px] w-full resize-none py-2 text-sm"
            }
            value={newServiceData.description}
            onChange={(event) => {
              setNewServiceData((prev) => ({
                ...prev,
                description: event.target.value,
              }));
            }}
          />
        </div>

        <div className={"mb-4 px-6"}>
          <div className={"mb-2 text-sm"}>Rate</div>
          {renderRateInput()}
          {errorRate && (
            <div className={"mt-2 text-sm font-semibold text-error"}>
              {errorRate}
            </div>
          )}
        </div>

        <div
          className={"mt-4 flex justify-end gap-3 p-3 text-sm font-semibold"}>
          <button
            disabled={isLoading}
            onClick={closeAddModal}
            className={
              "flex h-[32px] w-[100px] items-center justify-center rounded-lg border border-[#C8D2DE] px-4 py-2 font-Manrope text-sm text-[#253747] disabled:opacity-75"
            }>
            Cancel
          </button>

          <button
            disabled={isLoading}
            onClick={() => {
              if (!validateServiceForm()) return;

              addService({
                name: newServiceData.serviceName,
                description: newServiceData.description,
                rate: newServiceData.rate,
                orgId,
              });

              closeAddModal();
            }}
            className={
              "flex h-[32px] w-[100px] items-center justify-center rounded-lg bg-[#7c66f0] px-4 py-2 font-Manrope text-sm text-white disabled:opacity-75"
            }>
            Save
          </button>
        </div>
      </div>
    </div>
  );

  const renderUpdate = () => (
    <div className={"modal modal-open"}>
      <div className={"modal-box w-[450px] p-0"}>
        <div
          className={
            "mb-4 flex w-full items-center justify-start border-b border-gray-200 px-4 py-2 text-center text-base font-semibold"
          }>
          Update Service
        </div>

        <div className={"mb-4 px-6"}>
          <div className={"mb-2 text-sm"}>Service Name</div>
          <input
            maxLength={25}
            type={"text"}
            className={"input input-bordered h-[32px] w-full text-sm"}
            value={newServiceData.serviceName}
            onBlur={(event) => {
              if (!event.target.value.trim()) {
                setError("This field cannot be empty");
              } else {
                setError("");
              }
            }}
            onChange={(event) => {
              setNewServiceData((prev) => ({
                ...prev,
                serviceName: event.target.value,
              }));
            }}
          />
          {error && (
            <div className={"mt-2 text-sm font-semibold text-error"}>
              {error}
            </div>
          )}
        </div>

        <div className={"mb-4 px-6"}>
          <div className={"mb-2 text-sm"}>Description (optional)</div>
          <textarea
            maxLength={200}
            className={
              "input input-bordered h-[85px] w-full resize-none py-2 text-sm"
            }
            value={newServiceData.description}
            onChange={(event) => {
              setNewServiceData((prev) => ({
                ...prev,
                description: event.target.value,
              }));
            }}
          />
        </div>

        <div className={"mb-4 px-6"}>
          <div className={"mb-2 text-sm"}>Rate</div>
          {renderRateInput()}
          {errorRate && (
            <div className={"mt-2 text-sm font-semibold text-error"}>
              {errorRate}
            </div>
          )}
        </div>

        <div
          className={"mt-4 flex justify-end gap-3 p-3 text-sm font-semibold"}>
          <button
            disabled={isLoading}
            onClick={closeUpdateModal}
            className={
              "flex h-[32px] w-[100px] items-center justify-center rounded-lg border border-[#C8D2DE] px-4 py-2 font-Manrope text-sm text-[#253747] disabled:opacity-75"
            }>
            Cancel
          </button>

          <button
            disabled={isLoading}
            onClick={() => {
              if (!validateServiceForm()) return;

              updateService({
                id: newServiceData.id || "",
                name: newServiceData.serviceName,
                description: newServiceData.description,
                rate: newServiceData.rate,
                orgId,
              });

              closeUpdateModal();
            }}
            className={
              "flex h-[32px] w-[100px] items-center justify-center rounded-lg bg-[#7c66f0] px-4 py-2 font-Manrope text-sm text-white disabled:opacity-75"
            }>
            Save
          </button>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return <LottieLoading />;
  }

  return (
    <div className={"relative font-Manrope"}>
      <div className={"my-6 flex items-center gap-2"}>
        <SettingsIconButton
          hasShadow={false}
          icon={<ChevronLeft />}
          onClick={() => navigate(-1)}
        />
        <div className={"text-2xl font-semibold"}>Billing Settings</div>
      </div>

      <SettingsCard extraClasses={"p-4 w-full"}>
        <div className={"mb-4 flex justify-between"}>
          <div className={"text-lg font-semibold"}>Services</div>

          <Button
            buttonType={"button"}
            onClick={() => {
              setIsOpenAdd(true);
              resetServiceForm();
            }}
            size={"custom"}
            label={"Create Service"}
            extraClasses={"normal-case py-2 min-h-fit h-fit"}
          />
        </div>

        <Table
          data={services || []}
          columns={columns}
          initialSorting={[{ id: "name", desc: false }]}
        />
      </SettingsCard>

      {isOpenAdd && renderAdd()}
      {isOpenUpdate && renderUpdate()}
      {serviceToDelete && (
        <SettingsConfirmModal
          onClose={() => setServiceToDelete(null)}
          onConfirm={() => void handleDeleteService()}
          icon={<TrashSvg />}
          title={`Delete ‘${serviceToDelete.name}’ service?`}
          description={"Do you want to permanently delete this service?"}
          confirmText={isDeleting ? "Deleting..." : "Delete"}
          variant={"danger"}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};
